import dayjs from 'dayjs';
import { useRef, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useForm, Controller } from 'react-hook-form';

// Libs
import datepickerDateFormat from '@/lib/datepicker/dateFormat';
import { RANDOM_COMMENT_PICKER_TOOL } from '@/lib/routes/apiEndpoints';

// Utils
import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';
import useStateWithReset from '@/utils/hooks/utils/useStateWithReset';

// Components
import Input from '@/components/base/Input';
import Button from '@/components/base/Button';
import Label from '@/components/base/Label';
import InfoBanner from '@/components/base/InfoBanner/CompoundBanner';
import Icon from '@/components/base/Icon';
import Select from '@/components/base/Select';
import Checkbox from '@/components/base/Checkbox';
import Datepicker from '@/components/base/Datepicker';
import Timepicker from '@/components/base/Timepicker';
import SingleDatepickerInput from '@/components/base/Datepicker/SingleDatepickerInput';
import InputActionTag from '@/components/composite/Form/Input/InputActionTag';
import Tooltip from '@/components/base/Tooltip';

const nbWinnersOptions = [
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 }
];

const nbMentionsOptions = [
    { label: '0', value: 0 },
    { label: '1', value: 1 },
    { label: '2', value: 2 },
    { label: '3', value: 3 },
    { label: '4', value: 4 },
    { label: '5', value: 5 }
];

const RandomCommentsPickerForm = ({ media, socialProfile, onPickCallback, closePopover }) => {
    const { t } = useTranslation('commons');
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    // Ref Banners
    const refBannerInfo = useRef();
    const BannerInfoInViewPort = useIntersectionObserver(refBannerInfo, '0px');

    const userTimezone = user?.settings?.timezone ?? 'Europe/Paris';
    const currentDateTz = dayjs().utc().tz(userTimezone);
    const utc = currentDateTz.format('Z');

    const [formErrorMessage, setFormErrorMessage, resetFormErrorMessage] = useStateWithReset(null);

    const {
        control,
        register,
        handleSubmit,
        watch,
        getValues,
        setValue,
        formState: { errors, isSubmitting }
    } = useForm({
        defaultValues: {
            name: '',
            has_deadline: false,
            deadline_date: currentDateTz.format('YYYY-MM-DD'),
            deadline_hour: currentDateTz.format('HH:mm:00'),
            only_unique_comments: true,
            nb_winners: nbWinnersOptions[0].value,
            hashtags: [],
            nb_mentions: nbMentionsOptions[0].value
        }
    });

    const onSubmit = async (data) => {
        resetFormErrorMessage();

        try {
            const endpoint = injectEndpointUrlParams(RANDOM_COMMENT_PICKER_TOOL, {
                MEDIA_ID: media?.id
            });

            await post(endpoint, {
                social_profile_id: socialProfile.id,
                name: data.name,
                deadline: data.has_deadline
                    ? dayjs(
                          `${data.deadline_date}T${data.deadline_hour}${dayjs
                              .tz(`${data.deadline_date} ${data.deadline_hour}`, userTimezone)
                              .format('Z')}`
                      )
                          .utc()
                          .format('YYYY-MM-DD HH:mm:00')
                    : undefined,
                only_unique_comments: data.only_unique_comments,
                nb_winners: data.nb_winners,
                hashtags: data.hashtags.map((hashtag) => hashtag.substring(1)),
                nb_mentions: data.nb_mentions
            });
            onPickCallback();
            closePopover();
        } catch (e) {
            let errorMessage = t('errors:error_occured_submit_data');
            if (e.status === 422) {
                const errorsFields = e.response?.data?.errors;
                Object.keys(errorsFields).map((field) => {
                    const error_type = errorsFields[field][0];
                    if (field.split('.')[0] === 'hashtags' && error_type === 'min_string') {
                        errorMessage = t('errors:error_hashtag_min_length');
                    }
                });
            }

            setFormErrorMessage(errorMessage);
            // Scroll To info banner if it's not visible in the viewport
            if (!BannerInfoInViewPort) {
                refBannerInfo?.current?.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    useEffect(() => {
        for (const error of Object.values(errors)) {
            if (error?.ref) {
                error.ref.scrollIntoView({ behavior: 'smooth' });
                break;
            }
        }
    }, [errors]);

    return (
        <div>
            <p className="flex w-full items-center justify-center gap-3 border-b border-icoGray-300 py-5 font-GBold dark:border-icoDarkMode-beton">
                <Icon icon="trophy" className="text-icoBlue" />
                {t('random_comments_picker.title')}
            </p>
            <form className="space-y-5 p-8" onSubmit={handleSubmit(onSubmit)}>
                <div ref={refBannerInfo} />
                {formErrorMessage ? (
                    <InfoBanner>
                        <InfoBanner.Icon />
                        <InfoBanner.Content
                            title={t('errors:error_occured')}
                            message={formErrorMessage}
                        />
                    </InfoBanner>
                ) : null}
                <div>
                    <Label required htmlFor="name">
                        {t('random_comments_picker.contest_name')}
                    </Label>
                    <Input
                        id="name"
                        name="name"
                        placeholder={t('random_comments_picker.contest_name_placeholder')}
                        type="text"
                        disabled={isSubmitting}
                        error={errors?.name?.message}
                        {...register('name', {
                            required: t('errors:field_required'),
                            validate: (value) =>
                                value.trim().length > 0 || t('errors:field_not_empty')
                        })}
                    />
                </div>
                <div>
                    <div className="mb-3 flex items-center gap-3">
                        <Checkbox
                            id="has_deadline"
                            disabled={isSubmitting}
                            name="has_deadline"
                            {...register('has_deadline')}
                        />
                        <Label
                            htmlFor="has_deadline"
                            fontType="checkbox"
                            className="w-auto cursor-pointer">
                            {t('random_comments_picker.deadline')}
                            <RandomCommentsPickerFormTooltip
                                content={t('random_comments_picker.deadline_explanation')}
                            />
                        </Label>
                    </div>
                    <div className="flex flex-col gap-y-3 sm:flex-row sm:gap-x-5 sm:gap-y-0">
                        <div className="w-full sm:w-1/2">
                            <Controller
                                control={control}
                                name="deadline_date"
                                rules={{ required: watch('has_deadline') }}
                                render={({ field: { onChange, value } }) => (
                                    <Datepicker
                                        hasBorder
                                        className="!z-100"
                                        disabled={isSubmitting || !watch('has_deadline')}
                                        date={value}
                                        onChange={({ date }) => {
                                            if (date !== value) {
                                                onChange(dayjs(date[0]).format('YYYY-MM-DD'));
                                            }
                                        }}
                                        options={{
                                            time_24hr: user?.settings?.time_format === '24',
                                            maxDate: dayjs().format(),
                                            dateFormat:
                                                datepickerDateFormat[user?.settings.date_format] ??
                                                'Y/m/d',
                                            parseDate: (dateStr) => {
                                                return dayjs(dateStr).toDate();
                                            }
                                        }}>
                                        <SingleDatepickerInput
                                            id="deadline_date"
                                            required={watch('has_deadline')}
                                            disabled={isSubmitting || !watch('has_deadline')}
                                            error={
                                                errors?.deadline ? t('errors:field_required') : null
                                            }
                                        />
                                    </Datepicker>
                                )}
                            />
                        </div>
                        <div className="w-full sm:w-1/2">
                            <Controller
                                control={control}
                                name="deadline_hour"
                                rules={{ required: watch('has_deadline') }}
                                render={({ field: { onChange, value } }) => (
                                    <Timepicker
                                        time={value}
                                        hasBorder
                                        options={{
                                            time_24hr: user?.settings?.time_format === '24',
                                            dateFormat:
                                                datepickerDateFormat[
                                                    `${user.settings.time_format}_hour_minute`
                                                ] ?? 'H:i',
                                            maxTime:
                                                currentDateTz.format('YYYY-MM-DD') ===
                                                watch('deadline_date')
                                                    ? currentDateTz.format('HH:mm')
                                                    : null,
                                            minuteIncrement: 1
                                        }}
                                        onChange={(selectedDate) => {
                                            if (!selectedDate) return null;
                                            return onChange(dayjs(selectedDate).format('HH:mm:00'));
                                        }}
                                        afterInput={
                                            <div className="font-GMedium text-xxs">UTC {utc}</div>
                                        }
                                        disabled={isSubmitting || !watch('has_deadline')}
                                        fullWidth={true}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Checkbox
                        id="only_unique_comments"
                        disabled={isSubmitting}
                        name="only_unique_comments"
                        {...register('only_unique_comments')}
                    />
                    <Label
                        htmlFor="only_unique_comments"
                        fontType="checkbox"
                        className="w-auto cursor-pointer">
                        {t('random_comments_picker.get_unique_comments')}
                    </Label>
                </div>
                <div>
                    <Label required htmlFor="nb_winners">
                        {t('random_comments_picker.number_winners')}
                    </Label>
                    <Controller
                        control={control}
                        name="nb_winners"
                        render={({ field: { onChange, value } }) => (
                            <Select
                                selected={nbWinnersOptions.find((o) => o.value === value)}
                                options={nbWinnersOptions}
                                setSelected={(option) => onChange(option.value)}
                                disabled={isSubmitting}
                            />
                        )}
                    />
                </div>
                <div>
                    <Label htmlFor="hashtags">
                        {t('random_comments_picker.hashtags')}
                        <RandomCommentsPickerFormTooltip
                            content={t('random_comments_picker.hashtags_explanation')}
                        />
                    </Label>
                    <InputActionTag
                        id="hashtags"
                        name="hashtags"
                        placeholder={t('random_comments_picker.hashtags_placeholder')}
                        shouldClearSpace
                        additionalCheckOnEvent={(e) => e.target.value?.trim() !== '#'}
                        triggerEventList={[' ', 'space']}
                        onAddItem={(item) => {
                            const itemFormatted = item[0] === '#' ? item : `#${item}`;
                            if (!getValues('hashtags').includes(itemFormatted)) {
                                setValue('hashtags', [...getValues('hashtags'), itemFormatted]);
                            }
                        }}
                        onDeleteItem={(item) => {
                            setValue(
                                'hashtags',
                                getValues('hashtags').filter((hashtag) => hashtag !== item)
                            );
                        }}
                        tagList={watch('hashtags')}
                        disabled={isSubmitting}
                    />
                </div>
                <div>
                    <Label htmlFor="nb_mentions">
                        {t('random_comments_picker.number_mentions')}
                        <RandomCommentsPickerFormTooltip
                            content={t('random_comments_picker.number_mentions_explanation')}
                        />
                    </Label>
                    <Controller
                        control={control}
                        name="nb_mentions"
                        render={({ field: { onChange, value } }) => (
                            <Select
                                selected={nbMentionsOptions.find((o) => o.value === value)}
                                options={nbMentionsOptions}
                                setSelected={(option) => onChange(option.value)}
                                disabled={isSubmitting}
                            />
                        )}
                    />
                </div>
                <div className="flex items-center justify-between">
                    <button
                        disabled={isSubmitting}
                        className="font-GMedium text-xs text-icoGray-500 underline dark:text-icoDarkMode-wolf"
                        onClick={closePopover}>
                        {t('cancel')}
                    </button>
                    <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
                        {t('start')}
                    </Button>
                </div>
            </form>
        </div>
    );
};

const RandomCommentsPickerFormTooltip = ({ content }) => {
    const { t } = useTranslation('commons');

    return (
        <Tooltip content={content} placement="top">
            <Icon
                aria-label={t('infos')}
                icon="info"
                className="ml-1 align-middle text-xs leading-none text-icoGray-400 hover:text-icoGray-500 dark:text-icoDarkMode-wolf"
            />
        </Tooltip>
    );
};

export default RandomCommentsPickerForm;
